//Hooks
import { get } from 'svelte/store';
import { format } from 'svelte-i18n';
//Stores
import { user_data } from 'src/stores/account/user';
import { devices_settings, devices_status } from 'src/stores/device-runtime/status';
import { devices } from 'src/stores/lists/devices';
//Helpers
import { isBluTRV, real_id } from '../interface_helpers';
import { isPeripheralDevice } from '../addons/addon_device_helpers';
import { isXT1Module } from '../xt1_helpers/template_helpers';
import { postReq } from 'src/services/http';
import { sortOn } from '../utilities';
//Types
import type { Device, Devices } from '../../types/Device';
import type { VirtualComponentInterface, VirtualComponentTypes } from 'src/types/VirtualComponents';
import type { CloudApiResponse } from 'src/types/responses/CloudApiResponse';
import type { DeviceStatusG2 } from 'src/types/DeviceStatusG2';

const _devicesInProgress = {
	created: {},
	deleted: {},
};

export const VIRTUAL_DEVICE_CATEGORY = 'virtual_device';

export const isVirtualDevice = (deviceType: string): boolean => {
	return /^(virtual-group|bthome-device)$/.test(deviceType);
};

/**
 * Deletes a virtual device from the Cloud.
 * @param device - The virtual device to be deleted.
 * @returns A Promise containing the API response from the deletion request.
 * @throws If the device is already being deleted.
 */
export const deleteVirtualDevice = async (device: Device): Promise<CloudApiResponse> => {
	const id = device.id;
	if (_devicesInProgress.deleted[id]) {
		return;
	}
	_devicesInProgress.deleted[id] = 1;
	try {
		const res: CloudApiResponse = await postReq(`/interface/device/bulk_delete`, {
			ids: JSON.stringify([id]),
		});
		if (res?.isok) {
			devices.remove(id);
		}
		return res;
	} finally {
		delete _devicesInProgress.deleted[id];
	}
};

/**
 * Deletes all virtual children devices linked to a parent device.
 * @param device - The parent device.
 * @returns A Promise containing the API response from the deletion request.
 * @throws If any of the devices are already being deleted.
 */
export const deleteVirtualChildren = async (device: Device) => {
	const id = real_id(device.id);
	const allDevices = get(devices);
	for (const dev in allDevices) {
		if (new RegExp(`${id}_(32|22|12|2)\\d{2}$`).test(dev)) {
			await deleteVirtualDevice(allDevices[dev]);
		}
	}
};

/**
 * Creates a new virtual device on the Cloud.
 * @param {Device} device - The parent device of the virtual device.
 * @param {VirtualComponentInterface['config']} config - The virtual device configuration.
 * @param {string} component - The component type of the virtual device.
 * @param {VirtualDeviceTemplate} [virtualTemplate] - The virtual device template.
 * @returns A Promise containing the API response from the creation request.
 * @throws If the virtual device is already being created.
 */
export const createVirtualDevice = async (
	device: Device,
	config: VirtualComponentInterface['config'],
	component: string,
): Promise<CloudApiResponse> => {
	const id = real_id(device.id);
	const channel = getDeviceChannelByComponent(component);
	const vcDeviceID = `${id}_${channel}`;
	if (_devicesInProgress.created[vcDeviceID]) {
		return;
	}
	const positions = Object.values(get(devices) || [])
		.filter((v: Device) => v.room_id == device.room_id && Number.isFinite(v.position))
		.map((i) => i.position);
	const position = Math.max.apply(null, positions.length ? positions : [0]) + 1;
	const componentKind = component.split(':')[0];
	let type = config?.meta?.template?.type;
	if (!type) {
		switch (componentKind) {
			case 'blutrv':
				type = 'SBTR-001AEU';
				break;
			case 'irdevice':
				type = 'ir-device';
				break;
			case 'group':
				type = 'virtual-group';
				break;
			default:
				type = 'bthome-device';
		}
	}
	let category = VIRTUAL_DEVICE_CATEGORY;
	switch (componentKind) {
		case 'blutrv':
			category = 'thermostat';
			break;
		case 'irdevice':
			category = 'ir_controller';
			break;
	}
	const image =
		componentKind === 'blutrv'
			? 'images/device_images/SBTR-001AEU.png'
			: config?.meta?.ui?.icon || '';
	const vcDevice: Device = {
		...device,
		id: vcDeviceID,
		channel,
		type,
		name:
			config.name ||
			(componentKind === 'blutrv' ? `TRV (${Number(component.split(':20')[1]) + 1})` : component),
		category,
		image,
		createdAt: Date.now(),
		position,
	};
	if (!image) {
		vcDevice.icon = componentKind === 'irdevice' ? 'fas fa-remote' : 'far fa-cube';
	}
	if (config?.addr) {
		// BTHome device MAC address, used in the DeviceInfo component
		vcDevice.addr = config.addr;
	}

	_devicesInProgress.created[vcDeviceID] = 1;
	try {
		const res: CloudApiResponse = await postReq(`/interface/device/save`, {
			id: vcDeviceID,
			data: JSON.stringify(vcDevice),
		});
		if (res?.isok) {
			devices.update(vcDevice);
		}
		return res;
	} finally {
		delete _devicesInProgress.created[vcDeviceID];
	}
};

/**
 * Returns a VirtualComponentInterface object based on the given deviceID and component.
 * @param {string} deviceID - The ID of the device
 * @param {string} component - The component name
 * @param {string} [name] - The name to use as the key for the VirtualComponentInterface object
 * @returns {VirtualComponentInterface} - The VirtualComponentInterface object
 */
export const getVirtualComponent = (
	deviceID: Device['id'],
	component: string,
	name?: string,
): VirtualComponentInterface => {
	const deviceSettings = devices_settings.getByRealId(deviceID);
	const deviceStatus = devices_status.getByRealId(deviceID);
	return {
		config: deviceSettings?.[component],
		status: deviceStatus?.[component],
		key: name || component,
	};
};

/**
 * Returns an array of virtual component names from the device settings based on the given component types.
 * @param {string} deviceID - The ID of the device
 * @param {string} components - The component types to filter by, e.g. 'boolean|enum|text|button|number|bthomedevice|bthomesensor'
 * @returns {string[]} - An array of virtual component names
 */
export const getVirtualComponents = (deviceID: Device['id'], components: string): string[] => {
	const deviceSettings = devices_settings.getByRealId(deviceID);
	return Object.keys(deviceSettings || {})
		.filter((key) => new RegExp(`^(${components}):2\\d{2}$`).test(key))
		.sort();
};

/**
 * Returns an array of VirtualComponentInterface objects based on the given deviceID and component types.
 * @param {string} deviceID - The ID of the device
 * @param {string} [components] - The component types to filter by, e.g. 'boolean|enum|text|button|number|bthomedevice|bthomesensor'
 * @returns {VirtualComponentInterface[]} - An array of VirtualComponentInterface objects
 */
export const getVirtualComponentsInterface = (
	deviceID: Device['id'],
	components: string = 'boolean|enum|text|button|number|bthomedevice|bthomesensor',
): VirtualComponentInterface[] => {
	const deviceSettings = devices_settings.getByRealId(deviceID);
	const deviceStatus = devices_status.getByRealId(deviceID);
	return getVirtualComponents(deviceID, components).map((i) => {
		const status = deviceStatus?.[i];
		const config = deviceSettings?.[i];
		if (config) config.role = status?._attrs?.role;
		return {
			config,
			status,
			key: i,
			type: i.split(':')[0] as VirtualComponentTypes,
		};
	});
};

/**
 * Returns the device channel associated with the given component.
 * The device channel is determined by the type of component and the channel number in the component name.
 * The devices created from the Blu TRV components will start from channel 2200,
 * The devices created from the BT Home components will start from channel 1200,
 * The devices created from the IR device components will start from channel 3200,
 * while devices created from the group virtual component will start from channel 200.
 * @param {string} component - The component name
 * @returns {number} - The device channel
 */
export const getDeviceChannelByComponent = (component: string): number => {
	// *! The devices created from the Blu TRV components will start from channel 2200,
	// *! The devices created from the BT Home components will start from channel 1200,
	// *! The devices created from the IR device components will start from channel 3200,
	// *! while devices created from the group virtual component will start from channel 200.
	const deviceChannel = Number(component.split(':')[1]) || 0;
	if (/^(bthomedevice)/.test(component)) {
		return deviceChannel + 1000;
	} else if (/^(blutrv)/.test(component)) {
		return deviceChannel + 2000;
	} else if (/^(irdevice)/.test(component)) {
		return deviceChannel + 3000;
	}
	return deviceChannel;
};

export const getDeviceIDByComponent = (deviceID: Device['id'], component: string): string => {
	return `${real_id(deviceID)}_${getDeviceChannelByComponent(component)}`;
};

/**
 * Returns the component name associated with the given device.
 * The component name is determined by the type of device and the channel number in the device name.
 * The devices created from the Blu TRV components will start from channel 2200,
 * The devices created from the BT Home components will start from channel 1200,
 * The devices created from the IR device components will start from channel 3200,
 * while devices created from the group virtual component will start from channel 200.
 * @param {Device} device - The device object
 * @returns {string} - The component name associated with the device
 */
export const getComponentByDevice = (device: Device): string => {
	if (device?.type === 'ir-device') {
		return `irdevice:${device.channel - 3000}`;
	} else if (device?.channel >= 2200) {
		return `blutrv:${device.channel - 2000}`;
	} else if (device?.channel >= 1200) {
		return `bthomedevice:${device.channel - 1000}`;
	} else if (device?.channel >= 200) {
		return `group:${device.channel}`;
	} else if (device?.channel >= 100) {
		return device.component;
	}
	return null;
};

/**
 * Check if this is the first created Virtual device. Skips shared devices.
 * Note: The first Virtual Device is always-free, this functions is used to distinguish the premium vs (free) non-premium Virtual Devices.
 * @param device
 * @returns	true if this is the first Virtual Device
 */
export const isFirstVirtualDevice = (device: Device): boolean => {
	const firstVirtualDevice = sortOn(
		Object.values(get(devices)).filter((i) => /_(12|2)\d{2}$/.test(i.id) && !i?.shared),
		'createdAt',
	)?.[0];
	return firstVirtualDevice?.id == device?.id;
};

/**
 * Check if the Virtual Device is accessible, in relevance of the user's Premium status.
 * The first VD is always free and accessible, the rest are accessible only if the user is Premium.
 * Checks the premium status of the user and the number of Virtual Devices created.
 * @param device {Device} - The Virtual Device to check
 * @returns {boolean} true if the device is accessible, false if it is not accessible
 */
export const accessibleVirtualDevice = (device: Device): boolean => {
	if (
		device?.category == VIRTUAL_DEVICE_CATEGORY && //Only Virtual Devices
		!device?.shared && //Skip shared devices
		!accessibleVirtualDevices() // Check if the user is Premium and have access to more than 1 Virtual Device
	) {
		return isFirstVirtualDevice(device);
	}
	return true; // All other devices are accessible
};

/**
 * Retrieves the list of all Virtual device IDs excluding BluTRV, with an optional filter for excluding shared devices.
 *
 * @param {boolean} [excludeShared=false] - If `true`, shared devices are excluded from the results.
 * @returns {Device['id'][]} An array of Virtual device IDs matching the parent device.
 */
export const getAllVirtualDevices = (excludeShared = false): Device['id'][] => {
	const allDevices = get(devices);
	return Object.keys(allDevices).filter(
		(i) => /_(12|2)\d{2}$/.test(i) && (!excludeShared || !allDevices[i]?.shared),
	);
};

/**
 * Retrieves Virtual, Peripheral or BluTRV devices associated with a parent device ID.
 *
 * @param {Device['id']} parentID - The ID of the parent device.
 * @param {Devices} devices - An object containing all devices
 * @returns {Device['id'][]} An array of Virtual, Peripheral or BluTRV device IDs matching the parent device.
 */
export const getVirtualDevicesByParentID = (
	parentID: Device['id'],
	devices: Devices,
): Device['id'][] => {
	return Object.keys(devices).filter((i) => new RegExp(`${parentID}_(1|2|12|22|32)\\d{2}$`).test(i));
};

export const accessibleVirtualDevices = (): boolean => {
	return get(user_data).premium == 1 || getAllVirtualDevices(true).length < 1;
};

export const virtualBthomeTranslationValues = (deviceType) =>
	isXT1Module(deviceType)
		? get(format)('settings_title_bthome_components', {
				default: 'BTHome components',
			})
		: get(format)('settings_title_virtual_components', {
				default: 'Virtual components',
			});

/**
 * Checks if a device component is present in the device status.
 * @param {Device} device - The device to check.
 * @param {DeviceStatusG2} status - The device status to check.
 * @returns {boolean} True if the device component is present in the device status, false otherwise.
 */
export const isExtractedDeviceComponentMissing = (device: Device, status: DeviceStatusG2): boolean => {
	const isExtractedDevice =
		isBluTRV(device?.type) || isVirtualDevice(device?.type) || isPeripheralDevice(device?.type);
	if (device?.type === 'ir-device') {
		const settings = devices_settings.getByRealId(device.id);
		return settings?.[getComponentByDevice(device)] === undefined;
	}
	return isExtractedDevice && status?.[getComponentByDevice(device)] === undefined;
};
